import * as Sentry from "@sentry/react"
import { Alert } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Combobox } from "design-system/organisms/Combobox"
import { useShopifyCloneSizeChartMutation } from "modules/settings/hooks/shopify/useShopifyCloneSizeChartMutation"
import { useShopifyMetafieldDefinitionsQuery } from "modules/settings/hooks/shopify/useShopifyMetafieldDefinitionsQuery"
import { useShopifySearchProductsQuery } from "modules/settings/hooks/shopify/useShopifySearchProductsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ConvertSizeChartPermutConfigSchema,
  ConvertSizeChartResponseData,
  ShopifyMetafieldDefinitionsResponseDataSchema,
  ShopifyProductsResponseDataSchema,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import type { GeneratedSizeChartImage } from "./ConvertSizeChartGenerateImageForm"

type ConvertSizeChartResult = z.infer<typeof ConvertSizeChartResponseData>
type ShopifyMetafieldDefinition = z.infer<
  typeof ShopifyMetafieldDefinitionsResponseDataSchema
>[number]
type ShopifyProduct = z.infer<typeof ShopifyProductsResponseDataSchema>[number]
type ShopifyProductVariant = NonNullable<ShopifyProduct["variants"]>[number]
type MetafieldOption = ShopifyMetafieldDefinition & {
  label: string
  value: string
}
type ProductOption = {
  value: string
  label: string
  image?: string | null
  product: ShopifyProduct
}
type ProductSelection = {
  value: string
}

type Props = {
  isOpen: boolean
  onCloned: (data: {
    generatedImage: GeneratedSizeChartImage
    result: ConvertSizeChartResult
  }) => void
  onOpenChange: (isOpen: boolean) => void
}

const PRODUCT_METAFIELD_VALUE = "__product__"
const IMAGE_METAFIELD_TYPE = "file_reference"
const PERMUT_CONFIG_METAFIELD_KEY = "permut_config"
const SIZE_CHART_METAFIELD_TYPE = "json"

function getVariantLabel(variant: ShopifyProductVariant) {
  if (variant.title && variant.title !== "Default Title") {
    return variant.title
  }

  const options = variant.selectedOptions.map((option) => option.value).filter(Boolean)
  if (options.length > 0) return options.join(" / ")

  return variant.sku ?? variant.id
}

function getProductLabel(product: ShopifyProduct) {
  return product.status === "DRAFT" ? `[DRAFT] ${product.title}` : product.title
}

function isDefaultOnlyVariant(variant: ShopifyProductVariant) {
  const hasDefaultTitle = !variant.title || variant.title === "Default Title"
  const hasDefaultOptions =
    variant.selectedOptions.length === 0 ||
    variant.selectedOptions.every(
      (option) => option.name === "Title" && option.value === "Default Title",
    )

  return hasDefaultTitle && hasDefaultOptions
}

function getMetafieldOptionLabel(definition: ShopifyMetafieldDefinition) {
  if (!definition.name || definition.name === definition.key) {
    return `${definition.key} (${definition.type})`
  }

  return `${definition.name} - ${definition.key} (${definition.type})`
}

function isPermutConfigMetafieldKey(key: string) {
  return key === PERMUT_CONFIG_METAFIELD_KEY || key.endsWith(`.${PERMUT_CONFIG_METAFIELD_KEY}`)
}

function getUnifiedMetafieldOptions(
  productMetafields: ShopifyMetafieldDefinition[] = [],
  variantMetafields: ShopifyMetafieldDefinition[] = [],
  type?: string,
) {
  const definitions = new Map<string, MetafieldOption>()

  for (const definition of [...productMetafields, ...variantMetafields]) {
    if (type && definition.type !== type) continue
    if (isPermutConfigMetafieldKey(definition.key)) continue
    if (definitions.has(definition.key)) continue

    definitions.set(definition.key, {
      ...definition,
      label: getMetafieldOptionLabel(definition),
      value: definition.key,
    })
  }

  return [...definitions.values()].sort((a, b) => a.label.localeCompare(b.label))
}

function parseSizeChartResult(metafieldValue: string) {
  return ConvertSizeChartResponseData.parse({
    collection: "",
    styleName: "",
    result: JSON.parse(metafieldValue) as unknown,
  })
}

function parsePermutConfig(permutConfig: string) {
  const config = ConvertSizeChartPermutConfigSchema.parse(JSON.parse(permutConfig) as unknown)

  return {
    result: ConvertSizeChartResponseData.parse({
      collection: "",
      styleName: "",
      result: config.table,
    }),
    generatedImage: {
      base64Image: null,
      dimensions: config.labels,
      imageTransform: config.imageTransform,
      sourceImage: config.imageUrl,
      url: config.imageUrl,
    },
  }
}

export function CloneExistingSizeChartDialog({ isOpen, onCloned, onOpenChange }: Props) {
  const { t } = useTranslation()
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null)
  const [productSearchQuery, setProductSearchQuery] = useState("")
  const [variantId, setVariantId] = useState<string | undefined>()
  const [permutConfig, setPermutConfig] = useState<string | null>(null)
  const [hasCheckedPermutConfig, setHasCheckedPermutConfig] = useState(false)
  const [sizeChartMetafieldKey, setSizeChartMetafieldKey] = useState("")
  const [imageMetafieldKey, setImageMetafieldKey] = useState("")
  const normalizedProductSearchQuery = productSearchQuery.trim().replace(/\s+/g, " ")
  const debouncedProductSearchQuery = useDebouncedValue({
    value: normalizedProductSearchQuery,
  })

  const { mutateAsync: inspectSizeChartCloneSource, isPending: isInspectingCloneSource } =
    useShopifyCloneSizeChartMutation()
  const { mutateAsync: cloneSizeChart, isPending: isCloning } = useShopifyCloneSizeChartMutation()
  const { data: products, isFetching: isFetchingProducts } = useShopifySearchProductsQuery({
    enabled: isOpen,
    includeDraft: true,
    query: debouncedProductSearchQuery,
  })
  const { data: productMetafields, isFetching: isFetchingProductMetafields } =
    useShopifyMetafieldDefinitionsQuery(isOpen ? "product" : null)
  const { data: variantMetafields, isFetching: isFetchingVariantMetafields } =
    useShopifyMetafieldDefinitionsQuery(isOpen ? "productVariant" : null)
  const isFetchingMetafields = isFetchingProductMetafields || isFetchingVariantMetafields
  const isWaitingForProducts =
    isOpen && (normalizedProductSearchQuery !== debouncedProductSearchQuery || isFetchingProducts)

  const productOptions: ProductOption[] =
    normalizedProductSearchQuery !== debouncedProductSearchQuery
      ? []
      : (products ?? []).map((product) => ({
          value: product.id,
          label: getProductLabel(product),
          image: product.imageUrl,
          product,
        }))

  const selectedProductOption = selectedProduct
    ? {
        value: selectedProduct.id,
        label: getProductLabel(selectedProduct),
        image: selectedProduct.imageUrl,
        product: selectedProduct,
      }
    : undefined
  const selectedProductId = selectedProduct?.id
  const variants = selectedProduct?.variants ?? []
  const firstVariant = variants[0]
  const visibleVariants = firstVariant && isDefaultOnlyVariant(firstVariant) ? [] : variants
  const sizeChartMetafieldOptions = useMemo(
    () =>
      getUnifiedMetafieldOptions(productMetafields, variantMetafields, SIZE_CHART_METAFIELD_TYPE),
    [productMetafields, variantMetafields],
  )
  const imageMetafieldOptions = useMemo(
    () => getUnifiedMetafieldOptions(productMetafields, variantMetafields, IMAGE_METAFIELD_TYPE),
    [productMetafields, variantMetafields],
  )
  const hasPermutConfig = Boolean(permutConfig?.trim())
  const shouldShowLegacyCloneFields = Boolean(
    selectedProduct && hasCheckedPermutConfig && !hasPermutConfig,
  )
  const isCloneDisabled =
    isCloning ||
    isInspectingCloneSource ||
    !selectedProduct ||
    !hasCheckedPermutConfig ||
    (shouldShowLegacyCloneFields && (!sizeChartMetafieldKey || !imageMetafieldKey))

  let emptyProductPlaceholder = t(
    "settings.integrations.shopify.convertSizeChart.form.productEmpty",
  )

  if (isWaitingForProducts) {
    emptyProductPlaceholder = t(
      "settings.integrations.shopify.convertSizeChart.form.productLoading",
    )
  } else if (normalizedProductSearchQuery.length === 0) {
    emptyProductPlaceholder = t("settings.integrations.shopify.convertSizeChart.form.productIdle")
  }

  const resetState = () => {
    setSelectedProduct(null)
    setProductSearchQuery("")
    setVariantId(undefined)
    setPermutConfig(null)
    setHasCheckedPermutConfig(false)
    setSizeChartMetafieldKey("")
    setImageMetafieldKey("")
  }

  const handleOpenChange = (nextIsOpen: boolean) => {
    onOpenChange(nextIsOpen)
    if (!nextIsOpen) resetState()
  }

  const handleProductSearchQueryChange = (value: string) => {
    setProductSearchQuery(value)

    if (selectedProduct && value !== selectedProduct.title) {
      setSelectedProduct(null)
      setVariantId(undefined)
      setPermutConfig(null)
      setHasCheckedPermutConfig(false)
    }
  }

  const handleProductChange = (option: ProductSelection | undefined) => {
    const product =
      productOptions.find((candidate) => candidate.value === option?.value)?.product ??
      (selectedProduct?.id === option?.value ? selectedProduct : null)

    setSelectedProduct(product)
    setVariantId(undefined)
    setPermutConfig(null)
    setHasCheckedPermutConfig(false)
    setProductSearchQuery(product?.title ?? "")
  }

  const handleVariantChange = (value: string) => {
    setVariantId(value === PRODUCT_METAFIELD_VALUE ? undefined : value)
    setPermutConfig(null)
    setHasCheckedPermutConfig(false)
    setSizeChartMetafieldKey("")
    setImageMetafieldKey("")
  }

  useEffect(() => {
    if (!isOpen || !selectedProductId) {
      setPermutConfig(null)
      setHasCheckedPermutConfig(false)
      return
    }

    let isCurrent = true
    setPermutConfig(null)
    setHasCheckedPermutConfig(false)

    inspectSizeChartCloneSource({
      productId: selectedProductId,
      variantId,
    })
      .then((response) => {
        if (!isCurrent) return
        setPermutConfig(response.permutConfig?.trim() ? response.permutConfig : null)
        setHasCheckedPermutConfig(true)
      })
      .catch((error) => {
        if (!isCurrent) return
        setHasCheckedPermutConfig(true)
        Sentry.captureException(error, {
          extra: {
            context: "Failed to inspect Shopify size chart config",
            productId: selectedProductId,
            variantId,
          },
        })
      })

    return () => {
      isCurrent = false
    }
  }, [inspectSizeChartCloneSource, isOpen, selectedProductId, variantId])

  const handleClone = async () => {
    if (!selectedProduct) return

    try {
      const response = hasPermutConfig
        ? { imageUrl: null, metafieldValue: null, permutConfig }
        : await cloneSizeChart({
            imageMetafieldKey: imageMetafieldKey || undefined,
            productId: selectedProduct.id,
            sizeChartMetafieldKey: sizeChartMetafieldKey || undefined,
            variantId,
          })

      if (response.permutConfig) {
        const config = parsePermutConfig(response.permutConfig)

        onCloned(config)
        handleOpenChange(false)
        return
      }

      if (!response.metafieldValue || !response.imageUrl) {
        toast.error(t("settings.integrations.shopify.convertSizeChart.cloneMissingData"))
        return
      }

      onCloned({
        result: parseSizeChartResult(response.metafieldValue),
        generatedImage: {
          base64Image: response.imageUrl,
          dimensions: [],
          sourceImage: response.imageUrl,
          url: response.imageUrl,
        },
      })
      handleOpenChange(false)
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "Failed to clone Shopify size chart",
          imageMetafieldKey,
          productId: selectedProduct.id,
          sizeChartMetafieldKey,
          variantId,
        },
      })
      toast.error(t("settings.integrations.shopify.convertSizeChart.cloneError"))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {t("settings.integrations.shopify.convertSizeChart.cloneExistingProduct")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <label className="text-sm font-medium">
              {t("settings.integrations.shopify.convertSizeChart.form.product")}
            </label>
            <Combobox
              options={productOptions}
              value={selectedProductOption}
              onChange={handleProductChange}
              placeholder={t(
                "settings.integrations.shopify.convertSizeChart.form.productPlaceholder",
              )}
              emptyPlaceholder={emptyProductPlaceholder}
              searchPlaceholder={t(
                "settings.integrations.shopify.convertSizeChart.form.productSearch",
              )}
              searchQuery={productSearchQuery}
              setSearchQuery={handleProductSearchQueryChange}
              shouldFilter={false}
              className="w-full"
            />
          </div>
          {visibleVariants.length > 0 && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">
                {t("settings.integrations.shopify.convertSizeChart.form.variant")}
              </label>
              <Select
                value={variantId ?? PRODUCT_METAFIELD_VALUE}
                onValueChange={handleVariantChange}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "settings.integrations.shopify.convertSizeChart.form.variantPlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PRODUCT_METAFIELD_VALUE}>
                    {t("settings.integrations.shopify.convertSizeChart.form.productMetafield")}
                  </SelectItem>
                  {visibleVariants.map((variant) => (
                    <SelectItem key={variant.id} value={variant.id}>
                      {getVariantLabel(variant)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {shouldShowLegacyCloneFields && (
            <>
              <Alert variant="warning" className="whitespace-normal text-center">
                {t("settings.integrations.shopify.convertSizeChart.legacyCloneBackup")}
              </Alert>
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  {t("settings.integrations.shopify.convertSizeChart.form.sizeChartJson")}
                </label>
                <Select
                  value={sizeChartMetafieldKey}
                  onValueChange={setSizeChartMetafieldKey}
                  disabled={sizeChartMetafieldOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "settings.integrations.shopify.convertSizeChart.form.metafieldPlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sizeChartMetafieldOptions.map((metafield) => (
                      <SelectItem key={metafield.key} value={metafield.key}>
                        {metafield.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  {t("settings.integrations.shopify.convertSizeChart.form.imageMetafield")}
                </label>
                <Select
                  value={imageMetafieldKey}
                  onValueChange={setImageMetafieldKey}
                  disabled={imageMetafieldOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "settings.integrations.shopify.convertSizeChart.form.metafieldPlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {imageMetafieldOptions.map((metafield) => (
                      <SelectItem key={metafield.key} value={metafield.key}>
                        {metafield.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isFetchingMetafields &&
                (sizeChartMetafieldOptions.length === 0 || imageMetafieldOptions.length === 0) && (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "settings.integrations.shopify.convertSizeChart.form.cloneMetafieldsMissing",
                    )}
                  </p>
                )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t("shared.cancel")}
          </Button>
          <Button type="button" disabled={isCloneDisabled} onClick={handleClone}>
            {t("settings.integrations.shopify.convertSizeChart.form.clone")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
